(function($) {
    'use strict';


    // Small debounce helper
    function debounce(fn, wait) {
        let t; return function() { const ctx = this, args = arguments; clearTimeout(t); t = setTimeout(function(){ fn.apply(ctx, args); }, wait); };
    }

    // In-memory freshness map to avoid applying stale updates
    const freshness = new Map(); // key -> updatedAt
    function isStale(key, ts) { const last = freshness.get(key) || 0; return ts <= last; }
    function markFresh(key, ts) { freshness.set(key, ts); }

    // Namespace utilities
    const NS_VERSION = 'v1';
    function getNamespace() {
        try {
            // Prefer injected settingsNamespace on page (server-provided), else guest namespace
            if (window.settingsNamespace && typeof window.settingsNamespace === 'string') return window.settingsNamespace;
            const k = 'userSettings:guestNamespace:' + NS_VERSION;
            let v = localStorage.getItem(k);
            if (!v) { v = crypto.randomUUID ? crypto.randomUUID() : String(Math.random()).slice(2); localStorage.setItem(k, v); }
            return v;
        } catch (_) { return 'guest-' + NS_VERSION; }
    }

    function buildKey(parts) {
        const ns = getNamespace();
        const scope = parts.scope || 'global';
        const et = parts.entityType || '';
        const eid = parts.entityId || '';
        const key = parts.key || '';
        return ['userSettings', NS_VERSION, ns, scope, et, eid, key].join(':');
    }

    function parseKey(key) {
        const p = key.split(':');
        return { ns: p[2] || '', scope: p[3] || '', entityType: p[4] || '', entityId: p[5] || '', key: p[6] || '' };
    }

    // Transport layer
    function apiGet(params) { 
        // Check if user is authenticated before making API request
        // user-settings API requires authentication (require_auth=True)
        const isAuthenticated = (window.SERVER_AUTH_STATUS && window.SERVER_AUTH_STATUS.isAuthenticated) || window.settingsNamespace;
        if (!isAuthenticated) {
            // Return rejected promise for guests to prevent API calls
            return $.Deferred().reject(new Error('Authentication required')).promise();
        }
        
        // Normalize null/undefined to empty string for entity_type and entity_id
        // Backend expects empty string '' for global settings, not null
        const normalizedParams = {
            scope: params.scope,
            entity_type: (params.entity_type === null || params.entity_type === undefined) ? '' : params.entity_type,
            entity_id: (params.entity_id === null || params.entity_id === undefined) ? '' : params.entity_id
        };
        
        return $.ajax({ url: '/api/user-settings', method: 'GET', data: normalizedParams, dataType: 'json', cache: false }); 
    }
    function apiPut(payload) {
        // Check if user is authenticated before making API request
        // user-settings API requires authentication (require_auth=True)
        const isAuthenticated = (window.SERVER_AUTH_STATUS && window.SERVER_AUTH_STATUS.isAuthenticated) || window.settingsNamespace;
        if (!isAuthenticated) {
            // Return rejected promise for guests to prevent API calls
            return $.Deferred().reject(new Error('Authentication required')).promise();
        }
        
        // Validate payload before sending
        if (!payload || !payload.scope) {
            console.warn('[UserSettings] Invalid apiPut payload - missing scope:', payload);
            return $.Deferred().reject(new Error('Invalid payload: missing scope')).promise();
        }
        if (!payload.items || !Array.isArray(payload.items) || payload.items.length === 0) {
            console.warn('[UserSettings] Invalid apiPut payload - empty items array:', payload);
            return $.Deferred().reject(new Error('Invalid payload: empty items array')).promise();
        }
        // Validate each item
        const validItems = payload.items.filter(function(item) {
            if (!item || !item.key) {
                console.warn('[UserSettings] Skipping invalid item in apiPut:', item);
                return false;
            }
            return true;
        });
        if (validItems.length === 0) {
            console.warn('[UserSettings] No valid items in apiPut payload:', payload);
            return $.Deferred().reject(new Error('Invalid payload: no valid items')).promise();
        }
        return $.ajax({ 
            url: '/api/user-settings', 
            method: 'PUT', 
            data: JSON.stringify({ scope: payload.scope, items: validItems }), 
            contentType: 'application/json', 
            dataType: 'json' 
        });
    }

    // Publish updates cross-tabs
    const bc = ('BroadcastChannel' in window) ? new BroadcastChannel('userSettings') : null;
    function publish(key, value) {
        try { localStorage.setItem(key, JSON.stringify(value)); } catch (_) {}
        try { bc && bc.postMessage({ key: key, value: value }); } catch (_) {}
    }

    function applyUpdate(key, payload) {
        const ts = Number(payload && payload.updatedAt) || 0;
        if (isStale(key, ts)) return;
        markFresh(key, ts);
        // No direct UI side-effects here; consumers subscribe or query get()
    }

    // Listeners for cross-tab sync
    window.addEventListener('storage', function(e) {
        if (!e || e.storageArea !== localStorage || !e.key) return;
        if (!e.key.startsWith('userSettings:')) return;
        if (!e.newValue) return;
        try { const val = JSON.parse(e.newValue); applyUpdate(e.key, val); } catch(_) {}
    });
    if (bc) bc.addEventListener('message', function(ev){ const d = ev && ev.data; if (!d || !d.key) return; if (!String(d.key).startsWith('userSettings:')) return; applyUpdate(d.key, d.value || {}); });

    // Offline queue (best-effort)
    let pending = [];
    // Try to import guest keys into the authenticated namespace once after we obtain settingsNamespace
    function migrateGuestKeysIfNeeded(){
        try {
            if (!window.settingsNamespace) return;
            const ns = window.settingsNamespace;
            const prefixGuest = 'userSettings:' + NS_VERSION + ':'; // guest keys don't include user ns; filter conservative
            const toMigrate = [];
            for (let i=0;i<localStorage.length;i++){
                const k = localStorage.key(i);
                if (!k || !k.startsWith('userSettings:')) continue;
                // Skip already namespaced keys (contain ns value)
                if (k.indexOf(':'+ns+':') !== -1) continue;
                // Only migrate lectures scope keys
                if (k.indexOf(':lectures:lecture:') === -1) continue;
                try { const v = JSON.parse(localStorage.getItem(k) || 'null'); if (v) toMigrate.push({ k, v }); } catch(_){ }
            }
            if (!toMigrate.length) return;
            toMigrate.forEach(function(entry){
                const parts = parseKey(entry.k);
                const newKey = ['userSettings', NS_VERSION, ns, parts.scope, parts.entityType, parts.entityId, parts.key].join(':');
                // last-write-wins: just publish; server will reconcile by updatedAt
                publish(newKey, entry.v);
                try { localStorage.removeItem(entry.k); } catch(_){}
            });
        } catch(_){ }
    }
    function flushQueue() {
        if (!pending.length) return;
        const copy = pending.slice(); pending = [];
        const grouped = {};
        copy.forEach(function(item){
            // Validate item before adding to queue
            if (!item || !item.scope || !item.key) {
                console.warn('[UserSettings] Skipping invalid queue item:', item);
                return;
            }
            const k = item.scope;
            if (!grouped[k]) grouped[k] = [];
            // Normalize null/undefined to empty string for NOT NULL database columns
            // Backend expects empty string '' for global settings, not null
            const entityType = (item.entityType === null || item.entityType === undefined) ? '' : item.entityType;
            const entityId = (item.entityId === null || item.entityId === undefined) ? '' : item.entityId;
            grouped[k].push({ entity_type: entityType, entity_id: entityId, key: item.key, value: item.value });
        });
        
        // Only send requests for non-empty groups
        Object.keys(grouped).forEach(function(scope){
            if (grouped[scope].length === 0) {
                console.warn('[UserSettings] Skipping empty group for scope:', scope);
                return;
            }
            apiPut({ scope: scope, items: grouped[scope] }).catch(function(err){
                console.warn('[UserSettings] Failed to flush queue for scope:', scope, err);
                // Requeue on failure
                grouped[scope].forEach(function(it){ 
                    pending.push({ scope: scope, entityType: it.entity_type, entityId: it.entity_id, key: it.key, value: it.value }); 
                }); 
            });
        });
    }
    window.addEventListener('online', flushQueue);
    // Run migration when namespace becomes available (auth status fetched)
    if (document.readyState === 'complete' || document.readyState === 'interactive') {
        setTimeout(migrateGuestKeysIfNeeded, 0);
    } else {
        document.addEventListener('DOMContentLoaded', migrateGuestKeysIfNeeded);
    }
    
    // ✅ CRITICAL: Mark user settings as ready after initialization
    if(window.ComponentLoadingStatus){
      try {
        // Simple translations dictionary
        const translations = {
          'RU': 'Настройки пользователя загружены',
          'EN': 'User settings loaded',
          'DE': 'Benutzereinstellungen geladen'
        };
        // Get language from cookies/window (same logic as audio-lectures-loader-init.html)
        function getCurrentLang() {
          // Priority: window.LANG_FROM_SERVER > window.LANG > HTML lang > cookies > 'EN'
          if (window.LANG_FROM_SERVER) {
            const lang = window.LANG_FROM_SERVER.toUpperCase();
            if (lang === 'RU' || lang === 'EN' || lang === 'DE') return lang;
          }
          if (window.LANG) {
            const lang = window.LANG.toUpperCase();
            if (lang === 'RU' || lang === 'EN' || lang === 'DE') return lang;
          }
          const htmlLang = document.documentElement.getAttribute('lang');
          if (htmlLang) {
            const lang = htmlLang.toUpperCase();
            if (lang === 'RU' || lang === 'EN' || lang === 'DE') return lang;
          }
          const cookies = document.cookie.split(';');
          for (let i = 0; i < cookies.length; i++) {
            const cookie = cookies[i].trim();
            if (cookie.startsWith('preferred_language=')) {
              const langValue = cookie.substring(18).toUpperCase();
              if (langValue === 'RU' || langValue === 'EN' || langValue === 'DE') return langValue;
            }
          }
          return 'EN';
        }
        // Wait a bit for migration and language initialization to complete
        // Use longer delay to ensure window.LANG is set (it's set inline in HTML before this script)
        setTimeout(function(){
          const lang = getCurrentLang();
          const caption = translations[lang] || translations['EN'];
          window.ComponentLoadingStatus.update('userSettings', 'ready', caption);
        }, 300);
      } catch(_){}
    }

    // In-flight de-duplication for GETs
    // Use scope+entityType+entityId as key (not full localStorage key) because API returns all settings for scope
    const inflight = new Map(); // "scope|entityType|entityId" -> Promise
    
    function buildInflightKey(opts) {
        const scope = opts.scope || 'global';
        const et = opts.entityType || '';
        const eid = opts.entityId || '';
        return `${scope}|${et}|${eid}`;
    }

    // Public API
    const UserSettings = {
        get: function(opts) {
            // Normalize entityType and entityId to empty string if undefined/null
            // This ensures consistent API calls and proper caching
            if (opts.entityType === undefined || opts.entityType === null) {
                opts.entityType = '';
            }
            if (opts.entityId === undefined || opts.entityId === null) {
                opts.entityId = '';
            }
            
            const key = buildKey(opts);
            const forceRefresh = opts.forceRefresh === true;
            const maxCacheAge = opts.maxCacheAge || 0; // 0 = always check server, >0 = use cache if newer than this ms
            
            // If forceRefresh is true, skip localStorage check and always fetch from API
            if (!forceRefresh && maxCacheAge === 0) {
                // For critical data (like onboarding), always check server first
                // But for performance, we can use cache if data is fresh enough
                // Default behavior: check localStorage first, but allow override
            }
            
            // Check localStorage only if not forcing refresh and cache age allows it
            if (!forceRefresh) {
                try {
                    const raw = localStorage.getItem(key);
                    if (raw) {
                        const parsed = JSON.parse(raw);
                        const cachedAt = Number(parsed.updatedAt) || 0;
                        const now = Date.now();
                        
                        // If maxCacheAge is set and cache is fresh, use it
                        if (maxCacheAge > 0 && (now - cachedAt) < maxCacheAge) {
                            markFresh(key, cachedAt);
                            return $.Deferred().resolve(parsed).promise();
                        }
                        
                        // If maxCacheAge is 0 (default for critical data), still check cache
                        // but also fetch from server in background to update it
                        markFresh(key, cachedAt);
                        
                        // For critical data, still return cached value immediately but refresh in background
                        // This provides instant response while ensuring data freshness
                        if (maxCacheAge === 0) {
                            // Return cached value immediately, but also fetch fresh data
                            const cachedPromise = $.Deferred().resolve(parsed).promise();
                            
                            // Fetch fresh data in background (don't wait for it)
                            // Use inflight deduplication to prevent multiple requests
                            const inflightKey = buildInflightKey(opts);
                            if (!inflight.has(inflightKey)) {
                                const refreshPromise = apiGet({ 
                                    scope: opts.scope, 
                                    entity_type: opts.entityType || '', 
                                    entity_id: opts.entityId || '' 
                                }).then(function(resp){
                                    const data = resp && resp.data || {}; const map = data.settings || {};
                                    // Publish all settings from response
                                    Object.keys(map).forEach(function(k) {
                                        const settingKey = buildKey({ scope: opts.scope, entityType: opts.entityType, entityId: opts.entityId, key: k });
                                        const val = map[k];
                                        if (val) {
                                            val.updatedAt = val.updatedAt || Date.now();
                                            // Only update if server data is newer
                                            if (k === opts.key && (!cachedAt || val.updatedAt > cachedAt)) {
                                                publish(settingKey, val);
                                            } else if (k !== opts.key) {
                                                publish(settingKey, val);
                                            }
                                        }
                                    });
                                }).catch(function(){}).always(function(){ 
                                    // Delete from inflight after a delay to allow concurrent requests
                                    setTimeout(function() {
                                        inflight.delete(inflightKey);
                                    }, 100);
                                });
                                inflight.set(inflightKey, refreshPromise);
                            }
                            
                            return cachedPromise;
                        }
                        
                        return $.Deferred().resolve(parsed).promise();
                    }
                } catch(_) {}
            }
            
            // Fetch from API (either no cache, forceRefresh, or cache expired)
            const inflightKey = buildInflightKey(opts);
            
            // Check if there's an inflight getScope() request for the same scope/entityType/entityId
            // getScope() returns all settings, so we can extract our key from it
            if (inflight.has(inflightKey)) { 
                // Return existing promise, but extract our specific key from the response
                return inflight.get(inflightKey).then(function(response) {
                    // Response can be either:
                    // 1. Raw API response: { data: { settings: {...} } }
                    // 2. Processed getScope() result: { settings: {...}, globalSettings: {...} }
                    let map = {};
                    if (response && response.data) {
                        // Raw API response
                        map = response.data.settings || {};
                    } else if (response && typeof response === 'object') {
                        // Processed getScope() result
                        if (response.settings) {
                            map = response.settings;
                        } else {
                            // Direct map
                            map = response;
                        }
                    }
                    
                    const val = map[opts.key];
                    if (val) return val;
                    if (opts.default !== undefined) return opts.default;
                    return null;
                });
            }
            
            // Also check if there's a getScope() request for global settings (entityType='', entityId='')
            // that we can reuse when requesting global scope settings
            if (opts.entityType === '' && opts.entityId === '') {
                // Check if there's a lecture request that includes global_settings
                // We can't directly reuse it, but we can check localStorage cache first
                // The global_settings should already be cached by getScope() from the lecture request
            }
            const p = apiGet({ 
                scope: opts.scope, 
                entity_type: opts.entityType || '', 
                entity_id: opts.entityId || '' 
            }).then(function(resp){
                const data = resp && resp.data || {}; const map = data.settings || {};
                // Publish all settings from response to localStorage
                Object.keys(map).forEach(function(k) {
                    const settingKey = buildKey({ scope: opts.scope, entityType: opts.entityType || '', entityId: opts.entityId || '', key: k });
                    const val = map[k];
                    if (val) {
                        val.updatedAt = val.updatedAt || Date.now();
                        publish(settingKey, val);
                    }
                });
                // Return the full response so other concurrent requests can extract their keys
                return resp;
            }).catch(function(err) {
                // On error, return null (will be handled by caller's .then())
                return null;
            });
            
            // Store the promise that returns full response
            inflight.set(inflightKey, p);
            
            // Return promise that extracts the requested key
            return p.then(function(resp) {
                // Extract the requested key value from response
                if (!resp || !resp.data) {
                    if (opts.default !== undefined) return opts.default;
                    return null;
                }
                const map = resp.data.settings || {};
                const val = map[opts.key];
                if (val) return val;
                if (opts.default !== undefined) return opts.default;
                return null;
            }).always(function(){ 
                // Delete from inflight after a short delay to allow concurrent requests to use the same promise
                setTimeout(function() {
                    inflight.delete(inflightKey);
                }, 100);
            });
        },

        set: debounce(function(opts) {
            // Validate required fields
            if (!opts || !opts.scope || !opts.key) {
                console.warn('[UserSettings] Invalid set() call - missing required fields:', opts);
                return;
            }
            
            const key = buildKey(opts);
            const payload = Object.assign({}, opts.value || {});
            payload.updatedAt = Date.now();
            publish(key, payload);
            // Queue API write; coalesced by debounce
            // Normalize null/undefined to empty string for NOT NULL database columns
            // Backend expects empty string '' for global settings, not null
            const entityType = (opts.entityType === null || opts.entityType === undefined) ? '' : opts.entityType;
            const entityId = (opts.entityId === null || opts.entityId === undefined) ? '' : opts.entityId;
            const item = { scope: opts.scope, entityType: entityType, entityId: entityId, key: opts.key, value: payload };
            apiPut({ scope: opts.scope, items: [ { entity_type: entityType, entity_id: entityId, key: item.key, value: item.value } ] })
                .catch(function(err){
                    console.warn('[UserSettings] Failed to save setting:', err);
                    pending.push(item);
                });
        }, 800),

        getScope: function(opts) {
            const inflightKey = buildInflightKey(opts);

            // Check if there's an inflight request for the same scope/entityType/entityId
            if (inflight.has(inflightKey)) {
                return inflight.get(inflightKey).then(function(resp){
                    // Handle both raw API response and already processed result
                    let data, map;
                    if (resp && resp.data) {
                        // Raw API response
                        data = resp.data;
                        map = data.settings || {};
                    } else if (resp && typeof resp === 'object') {
                        // Already processed result (from previous call)
                        if (resp.settings) {
                            map = resp.settings;
                            data = { global_settings: resp.globalSettings };
                        } else {
                            map = resp;
                            data = {};
                        }
                    } else {
                        data = {};
                        map = {};
                    }
                    
                    Object.keys(map).forEach(function(k){ 
                        const lsKey = buildKey({ scope: opts.scope, entityType: opts.entityType, entityId: opts.entityId, key: k }); 
                        const v = map[k]; 
                        v.updatedAt = v.updatedAt || Date.now(); 
                        publish(lsKey, v); 
                    });
                    
                    // Process global scope settings if present (when entity_type/entity_id provided)
                    // API always includes global_settings when entity_type/entity_id are provided (even if empty)
                    if (opts.entityType && opts.entityId) {
                        const globalMap = data.global_settings || {};
                        // Cache global settings to localStorage
                        Object.keys(globalMap).forEach(function(k){ 
                            const lsKey = buildKey({ scope: opts.scope, entityType: '', entityId: '', key: k }); 
                            const v = globalMap[k]; 
                            v.updatedAt = v.updatedAt || Date.now(); 
                            publish(lsKey, v); 
                        });
                        // Always return both settings and globalSettings when entity_type/entity_id provided
                        return { settings: map, globalSettings: globalMap };
                    }
                    return map;
                });
            }
            
            // Check localStorage cache before making a new API request
            // This is especially important for global settings (entityType='', entityId='')
            // which are cached when a lecture request includes global_settings
            try {
                const namespace = window.settingsNamespace || 'guest';
                const prefix = `userSettings:v1:${namespace}:${opts.scope}:${opts.entityType || ''}:${opts.entityId || ''}:`;
                const cachedSettings = {};
                let hasCachedData = false;
                
                // Scan localStorage for all settings matching this scope/entityType/entityId
                for (let i = 0; i < localStorage.length; i++) {
                    const key = localStorage.key(i);
                    if (key && key.startsWith(prefix)) {
                        try {
                            const settingKey = key.split(':').pop(); // Get key name
                            const value = JSON.parse(localStorage.getItem(key) || 'null');
                            if (value) {
                                cachedSettings[settingKey] = value;
                                hasCachedData = true;
                            }
                        } catch(e) {
                            // Skip invalid entries
                        }
                    }
                }
                
                // If we have cached data, return it immediately (but still refresh in background)
                if (hasCachedData && Object.keys(cachedSettings).length > 0) {

                    // Return cached data immediately
                    const cachedPromise = $.Deferred().resolve(cachedSettings).promise();
                    
                    // Also refresh in background to ensure freshness
                    // Use inflight deduplication to prevent multiple requests
                    if (!inflight.has(inflightKey)) {
                        const refreshPromise = apiGet({ 
                            scope: opts.scope, 
                            entity_type: opts.entityType || '', 
                            entity_id: opts.entityId || '' 
                        }).then(function(resp){
                            const data = resp && resp.data || {}; 
                            const map = data.settings || {};
                            
                            // Process entity-specific settings
                            Object.keys(map).forEach(function(k){ 
                                const lsKey = buildKey({ scope: opts.scope, entityType: opts.entityType, entityId: opts.entityId, key: k }); 
                                const v = map[k]; 
                                v.updatedAt = v.updatedAt || Date.now(); 
                                publish(lsKey, v); 
                            });
                            
                            // Process global scope settings if present (when entity_type/entity_id provided)
                            // Global settings are stored with entityType='', entityId=''
                            // API always includes global_settings when entity_type/entity_id are provided (even if empty)
                            if (opts.entityType && opts.entityId) {
                                const globalMap = data.global_settings || {};
                                // Cache global settings to localStorage
                                Object.keys(globalMap).forEach(function(k){ 
                                    const lsKey = buildKey({ scope: opts.scope, entityType: '', entityId: '', key: k }); 
                                    const v = globalMap[k]; 
                                    v.updatedAt = v.updatedAt || Date.now(); 
                                    publish(lsKey, v); 
                                });
                                // Always return both settings and globalSettings when entity_type/entity_id provided
                                return { settings: map, globalSettings: globalMap };
                            }
                            
                            return map;
                        }).catch(function(){}).always(function(){ 
                            // Delete from inflight after a delay to allow concurrent requests
                            setTimeout(function() {
                                inflight.delete(inflightKey);
                            }, 500);
                        });
                        inflight.set(inflightKey, refreshPromise);
                    }
                    
                    return cachedPromise;
                }
            } catch(e) {
                console.warn('[UserSettings] getScope() - failed to check localStorage cache:', e);
            }
            const p = apiGet({ scope: opts.scope, entity_type: opts.entityType, entity_id: opts.entityId }).then(function(resp){
                const data = resp && resp.data || {}; 
                const map = data.settings || {};
                
                // Process entity-specific settings
                Object.keys(map).forEach(function(k){ 
                    const lsKey = buildKey({ scope: opts.scope, entityType: opts.entityType, entityId: opts.entityId, key: k }); 
                    const v = map[k]; 
                    v.updatedAt = v.updatedAt || Date.now(); 
                    publish(lsKey, v); 
                });
                
                // Process global scope settings if present (when entity_type/entity_id provided)
                // Global settings are stored with entityType='', entityId=''
                // API always includes global_settings when entity_type/entity_id are provided (even if empty)
                if (opts.entityType && opts.entityId) {
                    const globalMap = data.global_settings || {};
                    // Cache global settings to localStorage
                    Object.keys(globalMap).forEach(function(k){ 
                        const lsKey = buildKey({ scope: opts.scope, entityType: '', entityId: '', key: k }); 
                        const v = globalMap[k]; 
                        v.updatedAt = v.updatedAt || Date.now(); 
                        publish(lsKey, v); 
                    });
                    // Always return both settings and globalSettings when entity_type/entity_id provided
                    // This allows clients to reliably check for global settings
                    return { settings: map, globalSettings: globalMap };
                }
                
                return map;
            });
            inflight.set(inflightKey, p);
            p.always(function(){ 
                // Delay deletion to allow concurrent requests to reuse the same promise
                // Increased delay to 500ms to better handle rapid sequential requests
                setTimeout(function() {
                    inflight.delete(inflightKey);
                }, 500);
            });
            return p;
        }
    };

    // Expose to global namespace under SL (Scientology Library)
    window.SL = window.SL || {};
    window.SL.UserSettings = UserSettings;

})(jQuery);


